import { Box, Paper, styled } from '@mui/material';
import { ReactNode } from 'react';
import { AssetState } from '@/api/generatedApi';
import { BodyText } from '../BodyText';
import { TitleText } from '../TitleText';

const ZeroStateBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
});

export type AssetErrorState = AssetState.Unsupported | AssetState.Indexing | AssetState.No3DData | AssetState.Failed;

const FAILED_TO_INDEX_HEADING = 'Asset Failed to Index';

const getHeadingAndMessage = (state?: AssetErrorState, errorMessage?: string): { heading: string; message: string } => {
  if (state === AssetState.Unsupported) {
    return {
      heading: FAILED_TO_INDEX_HEADING,
      message: 'This asset version is unsupported and cannot be displayed in the viewer.',
    };
  }

  if (state === AssetState.Failed) {
    return {
      heading: FAILED_TO_INDEX_HEADING,
      message:
        'This asset could not be processed, so there is nothing to display in the viewer. Delete the asset and upload it again.',
    };
  }

  if (state === AssetState.Indexing) {
    return {
      heading: 'Indexing Asset',
      message:
        "We're processing your asset to enable search and matching features. This may take a few minutes depending on the size and complexity of the model. Please check back later.",
    };
  }

  if (state === AssetState.No3DData) {
    return {
      heading: 'No 3D Data',
      message: 'This asset was processed successfully, but it does not contain 3D data to display in the viewer.',
    };
  }

  return { heading: 'Error', message: errorMessage || 'An error occurred. Please try again later.' };
};

const DETAIL_PANEL_WIDTH = 520;
const MESSAGE_ONLY_WIDTH = 400;

export const AssetErrorMessage = ({
  assetState,
  errorMessage,
  children,
}: {
  assetState?: AssetErrorState;
  errorMessage?: string;
  children?: ReactNode;
}) => {
  const { heading, message } = getHeadingAndMessage(assetState, errorMessage);

  return (
    <ZeroStateBox>
      <Paper
        elevation={3}
        sx={{ padding: 4, maxWidth: children ? DETAIL_PANEL_WIDTH : MESSAGE_ONLY_WIDTH, textAlign: 'center' }}
      >
        <TitleText sx={{ marginBottom: 2 }}>{heading}</TitleText>
        <BodyText color='text.secondary'>{message}</BodyText>
        {children && <Box sx={{ marginTop: 2, display: 'flex', justifyContent: 'center' }}>{children}</Box>}
      </Paper>
    </ZeroStateBox>
  );
};
